import { forwardRef } from 'react'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { cn } from '@/lib/utils'
import { useWorkScaleClass } from '@/design-system/work-scale'

/**
 * Tooltip — всплывающая подсказка к короткой подписи (centrika-docs!33: у кратких названий окон
 * сводных показателей «Оценено»/«Не оценено»/«Уровни рисков» при наведении показывается полное
 * наименование). На Radix — как Select/Dialog: доступность из коробки (подсказка привязана к
 * триггеру через `aria-describedby`, открывается и по фокусу с клавиатуры, а не только по мыши,
 * закрывается по Escape) и позиционирование с учётом краёв окна.
 *
 * Композиция в стиле shadcn: Tooltip / TooltipTrigger / TooltipContent. `Tooltip` включает в себя
 * `Provider` Radix — примитив самодостаточен: подсказку можно поставить на любой странице, не
 * поднимая провайдер в каркасе (иначе компонент падал бы с «must be used within TooltipProvider»
 * везде, где страница рендерится отдельно, включая тесты и Storybook). `TooltipProvider` всё же
 * экспортирован: если понадобится общая политика задержек на группу подсказок, внешний провайдер
 * задаёт значения по умолчанию для вложенных.
 *
 * Подсказка — только пояснение, не единственный носитель смысла: её содержимое дублирует то, что
 * и так понятно из контекста (полное название рядом стоящей подписи).
 */
export const TooltipProvider = TooltipPrimitive.Provider

/**
 * Триггер подсказки — кнопка (фокусируема, значит подсказка открывается и с клавиатуры).
 * `type="button"` задан по умолчанию: Radix свой тип не ставит, а нативная кнопка без него —
 * `submit`, то есть подпись со подсказкой внутри формы отправляла бы форму (та же защита, что
 * у триггера `DateInput`).
 */
export const TooltipTrigger = forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>
>(({ type = 'button', ...props }, ref) => (
  <TooltipPrimitive.Trigger ref={ref} type={type} {...props} />
))
TooltipTrigger.displayName = 'TooltipTrigger'

export interface TooltipProps extends React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Root> {
  /** Задержка показа, мс. По умолчанию короткая — подсказка поясняет уже видимую подпись. */
  delayDuration?: number
}

export function Tooltip({ delayDuration = 200, children, ...props }: TooltipProps) {
  return (
    <TooltipPrimitive.Provider delayDuration={delayDuration}>
      <TooltipPrimitive.Root {...props}>{children}</TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  )
}

export const TooltipContent = forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 6, children, ...props }, ref) => {
  // Шкала шрифтов (centrika-docs!31): содержимое уходит в портал (document.body), то есть вне
  // DOM-поддерева рабочей области — класс шкалы объявляется на нём, как у SelectContent.
  const workScale = useWorkScaleClass()
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          // z-popover — тот же слой, что у списков выбора: подсказка обязана быть видна поверх
          // открытого диалога, иначе её перекроет панель.
          'z-popover rounded-8 bg-txt px-2.5 py-1.5 text-12 text-white shadow-elevated',
          workScale,
          className,
        )}
        {...props}
      >
        {children}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  )
})
TooltipContent.displayName = 'TooltipContent'
